import * as React from 'react'
import { SettingsSectionWithTabs } from '../../components/SettingsSectionWithTabs'
import { FieldsetSelect, Option as FieldsetSelectOption } from '../../components/FieldsetSelect'
import { FieldsetSwitch } from '../../components/FieldsetSwitch'

import { FieldsetInput } from '../../components/FieldsetInput'
import { useQueryTimesheetBasicSettings } from '@/lib/api/company/configuration/timesheet-basic/useQueryTimesheetBasicSettings'

import { useMutationTimesheetBasicSettings } from '@/lib/api/company/configuration/timesheet-basic/useMutationTimesheetBasicSettings'
import {
  TimesheetConfigurationModelParsed,
  TimesheetSettlementPeriodModelParsed,
} from '@/lib/api/company/configuration/timesheet-basic/types'
import { Button } from '@/ui'
import { useRestoreDefaultSettingsMutation } from '../../hooks/useRestoreDefaultSettingsMutation'
import { ConfigurationType } from '@/app/constants/configuration/configurationType'
import { faArrowRotateLeftSolid } from '@/lib/fa'
import {
  QUERY_KEY_SETTINGS_TIMESHEET_BASIC,
  QUERY_KEY_SETTINGS_TIMESHEET_SETTLEMENT_CYCLE,
} from '@/lib/api/constants'
import {
  START_DAY_OPTIONS,
  TIMESHEET_SCHEDULE_MODES,
  TIMESHEET_SETTLEMENT_TYPES,
  WEEK_FREE_DAYS,
  MINUTES_OPTIONS,
  WORKTIME_LIMIT_HOURS_OPTIONS,
  NIGHTTIME_START_HOURS_OPTIONS,
  HOLIDAY_START_HOURS_OPTIONS,
} from './options'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { getStartDayFromScheduleName } from '@/app/common/converters/configuration/startDayFromScheduleToTextConverter'
import { StartDayFromSchedule } from '@/app/constants/configuration/startDayFromSchedule'
import { TimesheetScheduleMode } from '@/app/constants/configuration/timesheetScheduleMode'
import { getTimesheetScheduleModeName } from '@/app/common/converters/configuration/timesheetScheduleModeToTextConverter'
import { getDayOfWeekNameByNumber } from '@/app/common/dateHelper'
import { TripleTimeLikeFields } from './TripleTimeLikeFields'
import { getTimesheetSettlementTypeName } from '@/app/common/converters/configuration/timesheetSettlementTypeToTextConverter'
import { FieldsetMonthYearPicker } from '../../components/FieldsetMonthYearPicker'
import { PeriodsTable } from './PeriodsTable'
import { formatDate } from '@/modules/shared/utils/date'
import { useWrapConfirm } from '@/modules/shared/hooks/useWrapConfirm'
import { useConfirm } from '@/modules/shared/context/ConfirmContext'
import { buildTimesheetBasicDiffLabels, TIMESHEET_BASIC_TAB_SCOPE } from './labels'
import { buildTimesheetBasicFormatters } from './formatters'
import axios from 'axios'
import { toast } from '@/lib/toasts/api'
import { SettingsPageWrapper } from '../../components/SettingsPageWrapper'
import { usePersistentTab } from '@/modules/shared/stores/tabsStore'
import { RouteComponentProps } from '@/app/navigation/withRouter'
// Basic timesheet configuration page
export const TimesheetBasicPageContent = ({
  match,
}: RouteComponentProps<{ companyId: string }>) => {
  const readOnly = false
  const companyId = match.params.companyId
  const [selectedTab, setSelectedTab] = usePersistentTab(
    TIMESHEET_BASIC_TAB_SCOPE,
    'a',
  )

  const wrapConfirm = useWrapConfirm()
  const confirm = useConfirm()

  const {
    data,
    isFetching,
    error: queryError,
  } = useQueryTimesheetBasicSettings(companyId)

  const {
    mutateAsync,
    isPending,
    error: mutationError,
  } = useMutationTimesheetBasicSettings(companyId)

  const restoreDefaults = useRestoreDefaultSettingsMutation(companyId, {
    configurationType: ConfigurationType.Timesheet,
    tabLabelKey: 'configuration-menu.timesheet-basic',
    queryKeys: [
      QUERY_KEY_SETTINGS_TIMESHEET_BASIC,
      QUERY_KEY_SETTINGS_TIMESHEET_SETTLEMENT_CYCLE,
    ],
  })

  const handleSave = async () => {
    if (state) {
      try {
        const resp = await mutateAsync(state)
        toast.success({ text: t('configuration.data-updated') })
      } catch (e) {
        if (axios.isAxiosError(e)) {
          const data = e.response?.data
          if (Array.isArray(data) && data.length > 0) {
            const code = data[0]?.code
            let errorMsg = code
            try {
              errorMsg = t(`errorCode.${errorMsg}`)
            } catch (e) {}
            console.log('API error code:', code, errorMsg)
            toast.error({ text: errorMsg, autoCloseMs: 5000 })
          } else {
            console.log('Axios error, but response data is not an array:', data)
            toast.error({ text: 'Unhandled Error' })
          }
        } else {
          // Non-Axios error
          console.error('Unknown error:', e)
          toast.error({ text: 'Unknown Error' })
        }
      }
    }
  }

  const handleReset = React.useCallback(() => {
    setState(data)
  }, [data])

  const [state, setState] =
    React.useState<TimesheetConfigurationModelParsed | null>(null)

  React.useEffect(() => {
    if (data) {
      setState(data)
    }
  }, [data])

  const { t } = useLocalize()

  const diffLabels = React.useMemo(() => buildTimesheetBasicDiffLabels(t), [t])

  const diffFormatters = React.useMemo(() => buildTimesheetBasicFormatters(t), [t])

  const handleChangeBoolean = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value, name, checked } = e.target
      setState((state) => ({
        ...state,
        [name]: checked,
      }))
    },
    [],
  )

  const applySettlementTypeChange = React.useCallback(
    (value: string, meta: { name: string; option: FieldsetSelectOption }) => {
      setState((state) => ({
        ...state,
        currentSettlementType: (meta.option?.key ?? value) as number,
      }))
    },
    [],
  )

  const confirmSettlementTypeChange = React.useMemo(
    () =>
      wrapConfirm(applySettlementTypeChange, {
        title: t('configuration.timesheet.changeSettlementPeriodTypeTitle'),
        message: t('configuration.timesheet.changeSettlementPeriodTypeDetails'),
      }),
    [wrapConfirm, applySettlementTypeChange, t],
  )

  const handleSelect = React.useCallback((value, { name, option }) => {
    switch (name) {
      case 'currentSettlementType':
        void confirmSettlementTypeChange(value, {
          name,
          option,
        })
        break
      case 'startDayFromSchedule':
      case 'prepareScheduleOnCalculation':
        setState((state) => ({
          ...state,
          [name]: value === '0' ? true : false,
        }))
        break
      case 'holidayStartTimeHours':
        setState((state) => {
          const parts = (state?.holidayStartTime || '00:00').split(':')
          const hh = String(value).padStart(2, '0')
          const mm = parts[1] || '00'
          return { ...state, holidayStartTime: `${hh}:${mm}` }
        })
        break
      case 'holidayStartTimeMinutes':
        setState((state) => {
          const parts = (state?.holidayStartTime || '00:00').split(':')
          const hh = parts[0] || '00'
          const mm = String(value).padStart(2, '0')
          return { ...state, holidayStartTime: `${hh}:${mm}` }
        })
        break
      default:
        setState((state) => ({
          ...state,
          [name]: option?.key ?? value,
        }))
        break
    }
  }, [confirmSettlementTypeChange])

  const handleInputChange = React.useCallback((e) => {
    const { name, value } = e.target
    setState((state) => ({
      ...state,
      [name]: value,
    }))
  }, [])

  const handleMonthPick = React.useCallback((value: string) => {
    const m = /^(\d{4})-(\d{2})/.exec(String(value).trim())
    if (m) {
      const y = Number(m[1])
      const mo = Number(m[2])
      setState((state) => ({
        ...state,
        firstSettlementPeriodStartDate: `${y}-${String(mo).padStart(2, '0')}-01`,
      }))
      return
    }
    const parsedDate = new Date(String(value))
    setState((state) => ({
      ...state,
      firstSettlementPeriodStartDate: formatDate(parsedDate, 'YYYY-MM-DD'),
    }))
  }, [])

  const handleRemovePeriod = React.useCallback(
    async (p: TimesheetSettlementPeriodModelParsed) => {
      const dateFrom = new Date(p.periodStartDate).getTime()
      const periods = state?.settlementPeriods ?? []
      const removed = periods.filter(
        (period) => new Date(period.periodStartDate).getTime() >= dateFrom,
      )
      if (removed.length === 0) return

      const sorted = [...removed].sort(
        (a, b) =>
          new Date(a.periodStartDate).getTime() -
          new Date(b.periodStartDate).getTime(),
      )
      const firstStart = formatDate(sorted[0].periodStartDate, 'YYYY-MM-DD')
      const lastEnd = formatDate(
        sorted[sorted.length - 1].periodEndDate,
        'YYYY-MM-DD',
      )

      const messageKey =
        removed.length === 1
          ? 'configuration.timesheet.deleteSettlementPeriodDetailsSingle'
          : 'configuration.timesheet.deleteSettlementPeriodDetailsRange'
      const message = t(messageKey)
        .replace('{0}', String(removed.length))
        .replace('{1}', firstStart)
        .replace('{2}', lastEnd)

      const ok = await confirm({
        title: t('configuration.timesheet.deleteSettlementPeriodTitle'),
        message,
      })
      if (!ok) return

      setState((state) => {
        if (!state || !Array.isArray(state.settlementPeriods))
          return state

        const newState = structuredClone(state)
        const settlementPeriods = structuredClone(state.settlementPeriods)
        const newSettlementPeriods = settlementPeriods.filter(
          (period) => new Date(period.periodStartDate).getTime() < dateFrom,
        )

        return {
          ...newState,
          settlementPeriods: newSettlementPeriods,
        }
      })
    },
    [state, confirm, t],
  )

  const isDirty = React.useMemo(() => {
    return JSON.stringify(state) !== JSON.stringify(data)
  }, [state, data])

  if (!state && !isPending && !isFetching) {
    return null // @TODO return error message
  }

  return (
    <SettingsPageWrapper
      isReady={!!state}
      isPending={isPending || isFetching}
      isError={!!queryError || !!mutationError}
      isDirty={isDirty}
      errorMsg={
        queryError
          ? t('configuration-timesheet.basic-load-error')
          : ''
      }
      actions={
        <>
          <Button
            disabled={isDirty}
            loading={restoreDefaults.isPending}
            className="tw-rounded-lg tw-px-4"
            variant="secondary"
            size="sm"
            faLeadingIcon={faArrowRotateLeftSolid}
            data-test-id="timesheet-basic-restore-defaults"
            text={t('configuration-menu.restore').toString()}
            onClick={wrapConfirm(() => restoreDefaults.mutate(), {
              message: restoreDefaults.confirmMessage,
            })}
          />
          <Button
            disabled={!isDirty}
            className="tw-rounded-lg tw-px-4 tw-text-content-secondary"
            variant="secondary"
            size="sm"
            onClick={wrapConfirm(handleReset, {
              message: t('configuration.reset-confirm'),
            })}
          >
            Reset
          </Button>
          <Button
            disabled={!isDirty}
            className="tw-rounded-lg tw-px-4"
            size="sm"
            onClick={wrapConfirm(handleSave, { message: t('configuration.save-confirm') })}
          >
            {t('common.save')}
          </Button>
        </>
      }
      globalErrorMsg={
        t('configuration-timesheet.basic-load-error')
      }
      diffBefore={(data ?? {}) as Record<string, unknown>}
      diffAfter={(state ?? {}) as Record<string, unknown>}
      diffLabels={diffLabels}
      diffFormatters={diffFormatters}
    >
      {state && (
        <SettingsSectionWithTabs
          title=""
          selected={selectedTab}
          setSelected={setSelectedTab}
        >
          <div
            data-tab-name="a"
            title={t('configuration-timesheet.tab-basic')}
            className="tw-flex tw-flex-col tw-gap-6 tw-p-2 tw-pb-8"
          >
            {/* [Radio] Optymalizacja dni pracy/wolnych -> Automatyczne wyznaczanie dni pracy/wolnych */}
            <FieldsetSwitch
              label={t('configuration-timesheet.auto-work-days')}
              checked={state.fitWorkingDays}
              onChange={handleChangeBoolean}
              name="fitWorkingDays"
              disabled={readOnly}
            />

            {/* [Radio] Dostosuj wymiar w dniach pustych -> Automatyczne dostosowywanie wymiaru w dniach pustych */}
            <FieldsetSwitch
              label={t('configuration-timesheet.auto-schedule-empty-days')}
              checked={state.useScheduleFromEmptyDays}
              onChange={handleChangeBoolean}
              name="useScheduleFromEmptyDays"
              disabled={readOnly}
            />

            {/* [Radio] Automatyczne rekompensowanie dodatków za nadgodziny -> Automatyczne oddawanie dodatków za nadgodziny */}
            <FieldsetSwitch
              label={t('configuration-timesheet.auto-recompense-overhours')}
              checked={state.recompenseOverhours}
              onChange={handleChangeBoolean}
              name="recompenseOverhours"
              disabled={readOnly}
            />

            {/* [Radio] Automatyczne rekompensowanie dodatków za dyżur 100% -> Automatyczne oddawanie dodatków za dyżur 100% */}
            <FieldsetSwitch
              label={t('configuration-timesheet.auto-return-duty100')}
              checked={state.returnDuty100}
              onChange={handleChangeBoolean}
              name="returnDuty100"
              disabled={readOnly}
            />

            {/* [Radio] Domyślne nadgodziny rekompensowane na wniosek pracownika -> Automatyczne oddawanie nadgodzin na wniosek pracownika */}
            <FieldsetSwitch
              label={t('configuration-timesheet.auto-overhours-at-request')}
              checked={state.overhoursAtTheRequest}
              onChange={handleChangeBoolean}
              name="overhoursAtTheRequest"
              disabled={readOnly}
            />

            {/* [Radio] Automatyczne rekompensowanie pracy w dni wolne -> Automatyczne oddawanie pracy w dni wolne */}
            <FieldsetSwitch
              label={t('configuration-timesheet.auto-recompense-free-day')}
              checked={state.recompenseWorkOnFreeDay}
              onChange={handleChangeBoolean}
              name="recompenseWorkOnFreeDay"
              disabled={readOnly}
            />

            {/* [Radio] Automatyczne uzupełnianie wymiaru dyżurem 50% z danego dnia */}
            <FieldsetSwitch
              label={t('configuration-timesheet.auto-duty50-to-worktime')}
              checked={state.duty50ToWorkTime}
              onChange={handleChangeBoolean}
              name="duty50ToWorkTime"
              disabled={readOnly}
            />

            {/* [Radio] Automatyczne podpowiadanie dni urlopowych */}
            <FieldsetSwitch
              label={t('configuration-timesheet.auto-insert-leave-days')}
              checked={state.setLeaveForWorkingDaysWithNoWork}
              onChange={handleChangeBoolean}
              name="setLeaveForWorkingDaysWithNoWork"
              disabled={readOnly}
            />

            {/* [Radio] Automatyczne wstawianie wolnego dnia za święto w sobotę -> Automatyczne obniżanie wymiaru za święto przypadające w dodatkowy dzień wolny np. sobota */}
            <FieldsetSwitch
              label={
                t('configuration-timesheet.auto-reduce-holiday-saturday')
              }
              checked={state.addFreeDayForHolidayOnSaturday}
              onChange={handleChangeBoolean}
              name="addFreeDayForHolidayOnSaturday"
              disabled={readOnly}
            />

            {/* [Radio] Przeliczaj uwzględniając wszystkie miesiące okresu rozliczeniowego -> Autamatycznie przeliczaj ewidencję uwzględniając wszystkie miesiące okresu rozliczeniowego */}
            <FieldsetSwitch
              label={
                t('configuration-timesheet.auto-calculate-all-months')
              }
              checked={state.calculateAllMonthsForPeriod}
              onChange={handleChangeBoolean}
              name="calculateAllMonthsForPeriod"
              disabled={readOnly}
            />
          </div>

          <div
            data-tab-name="b"
            title={t('configuration-timesheet.tab-settlement-params')}
            className="tw-flex tw-flex-col tw-gap-6 tw-p-2 tw-pb-8"
          >
            {/* 
        [Select] Rozpoczynaj dobę -> Rozpoczynaj doby pracownicze
          wg pierwszego zdarzenia po zakończeniu doby 24h
          wg harmonogramu
         */}
            <FieldsetSelect
              label={t('configuration-timesheet.start-working-days')}
              options={START_DAY_OPTIONS.map((value) => ({
                key: value.toString(),
                value: value.toString(),
                label: getStartDayFromScheduleName(value, t),
              }))}
              value={String(
                state.startDayFromSchedule
                  ? StartDayFromSchedule.StartDayFromScheduleOption
                  : StartDayFromSchedule.StartDayAfterFirstActivityInDayOption,
              )}
              handleChange={handleSelect}
              name="startDayFromSchedule"
              disabled={readOnly}
            />

            {/* [Select] Dostosowuj wymiar dni podczas przeliczania -> Dostosowuj wymiar dni podczas przeliczania w systemie równoważnym lub mieszanym czasu pracy */}
            <FieldsetSelect
              label={
                t('configuration-timesheet.adjust-days-equivalent-system')
              }
              options={TIMESHEET_SCHEDULE_MODES.map((value) => ({
                key: value.toString(),
                value: value.toString(),
                label: getTimesheetScheduleModeName(value, t),
              }))}
              value={String(
                state.prepareScheduleOnCalculation
                  ? TimesheetScheduleMode.AutoPrepareScheduleFromCalculation
                  : TimesheetScheduleMode.StartDayFromScheduleOption,
              )}
              handleChange={handleSelect}
              name="prepareScheduleOnCalculation"
              disabled={readOnly}
            />

            {/* [Select] Dodatkowy dzień wolny w tygodniu */}
            <FieldsetSelect
              label={t('configuration-timesheet.additional-free-day')}
              options={WEEK_FREE_DAYS.map((value) => ({
                key: value,
                value: value.toString(),
                label: t(getDayOfWeekNameByNumber(value)),
              }))}
              value={state.weekFreeDay?.toString()}
              handleChange={handleSelect}
              name="weekFreeDay"
              disabled={readOnly}
            />

            {/* {pole łączone} [Time picker] + "–" + [Time picker <disabled>] — Naliczaj dodatki za pracę w godzinach nocnych -> Naliczaj dodatki za pracę w godzinach nocnych zgodnie z Kodeksem Pracy art. 151 */}
            <TripleTimeLikeFields
              label={
                t('configuration-timesheet.night-work-kp-art151')
              }
              hour={{
                value: String(state.nightTimeStartHours),
                options: NIGHTTIME_START_HOURS_OPTIONS,
                name: 'nightTimeStartHours',
                handleChange: handleSelect,
              }}
              minute={{
                value: String(state.nightTimeStartMinutes),
                options: MINUTES_OPTIONS,
                name: 'nightTimeStartMinutes',
                handleChange: handleSelect,
              }}
              readonly={{
                value: state.nightTimeEndHour,
              }}
            />

            {/* {pole łączone} [Time picker] + "–" + [Time picker <disabled>] — Ograniczenie 10H pracy w porze nocnej (4H) -> Naliczaj dodatki za pracę w godzinach nocnych zgodnie z UoCPK art. 21 */}
            <TripleTimeLikeFields
              label={
                t('configuration-timesheet.night-work-uocpk-art21')
              }
              hour={{
                value: String(state.workTimeLimitIntervalStartTimeHours),
                options: WORKTIME_LIMIT_HOURS_OPTIONS,
                name: 'workTimeLimitIntervalStartTimeHours',
                handleChange: handleSelect,
              }}
              minute={{
                value: String(state.workTimeLimitIntervalStartTimeMinutes),
                options: MINUTES_OPTIONS,
                name: 'workTimeLimitIntervalStartTimeMinutes',
                handleChange: handleSelect,
              }}
              readonly={{
                value: state.workTimeLimitIntervalEndTime,
              }}
            />

            {/* {pole łączone} [Time picker] + [Time picker] — Doby niedzielno-świąteczne -> Początek doby niedzielno-świątecznej */}
            <TripleTimeLikeFields
              label={t('configuration-timesheet.holiday-start-time')}
              hour={{
                value: String(
                  parseInt(state.holidayStartTime?.split(':')[0] || '0'),
                ),
                options: HOLIDAY_START_HOURS_OPTIONS,
                name: 'holidayStartTimeHours',
                handleChange: handleSelect,
                disabled: readOnly,
              }}
              minute={{
                value: String(
                  parseInt(state.holidayStartTime?.split(':')[1] || '0'),
                ),
                options: MINUTES_OPTIONS,
                name: 'holidayStartTimeMinutes',
                handleChange: handleSelect,
                disabled: readOnly,
              }}
            />

            {/* [Input numeryczny] Roczny limit nadgodzin -> Limit nadgodzin w skali roku */}
            <FieldsetInput
              label={t('configuration-timesheet.yearly-overhours-limit')}
              type="number"
              value={(() => {
                const ts = state.overhoursLimit
                if (!ts) return ''
                const parts = ts.split('.')
                const days = parts.length === 2 ? parseInt(parts[0]) || 0 : 0
                const timePart = parts.length === 2 ? parts[1] : parts[0]
                const hours = parseInt(timePart?.split(':')[0]) || 0
                return String(days * 24 + hours)
              })()}
              onChange={(e) => {
                const raw = parseInt(e.target.value)
                if (isNaN(raw)) {
                  setState((s) => ({ ...s, overhoursLimit: '' }))
                  return
                }
                const hours = Math.min(416, Math.max(0, raw))
                const days = Math.floor(hours / 24)
                const remainingHours = hours % 24
                setState((s) => ({
                  ...s,
                  overhoursLimit: `${days}.${String(remainingHours).padStart(2, '0')}:00:00`,
                }))
              }}
              name="overhoursLimit"
              disabled={readOnly}
              min={0}
              max={416}
            />
          </div>

          <div
            data-tab-name="c"
            title={t('configuration-timesheet.tab-settlement-periods')}
            className="tw-flex tw-flex-col tw-gap-6 tw-p-2 tw-pb-8"
          >
            {/* [Select] Długość okresu rozliczeniowego */}
            <FieldsetSelect
              className="tw-z-50"
              label={t('configuration-timesheet.settlement-period-length')}
              options={TIMESHEET_SETTLEMENT_TYPES.map((value) => ({
                key: value,
                value: value.toString(),
                label: getTimesheetSettlementTypeName(value, t),
              }))}
              value={String(state.currentSettlementType)}
              handleChange={handleSelect}
              name="currentSettlementType"
              disabled={readOnly}
            />

            {/* [Date picker] Data początku pierwszego okresu rozliczeniowego -> Miesiąc początku okresu rozliczeniowego */}
            <FieldsetMonthYearPicker
              className="tw-z-10"
              label={t('configuration-timesheet.settlement-period-start-month')}
              value={state.firstSettlementPeriodStartDate}
              name="firstSettlementPeriodStartDate"
              placeholder=""
              handleChange={wrapConfirm(handleMonthPick, {
                title: t(
                  'configuration.timesheet.changeFirstSettlementPeriodStartDateTitle',
                ),
                message: t(
                  'configuration.timesheet.changeFirstSettlementPeriodStartDateDetails',
                ),
              })}
              disabled={readOnly}
            />

            <PeriodsTable
              periods={state.settlementPeriods}
              handleRemove={handleRemovePeriod}
            />
          </div>
        </SettingsSectionWithTabs>
      )}
    </SettingsPageWrapper>
  )
}
